import { request } from '@/lib/api'
import { asClearableString, makeDomainSave } from './domainSave'

/**
 * Контакты и участники сделки (тикет #98).
 *
 * Список контактов — механизм списков; здесь то, чего у него нет: создание целиком,
 * правка одной транзакцией с версией и роли людей в сделке.
 */

export type ParticipantRole =
  | 'decision_maker'
  | 'economic_buyer'
  | 'technical'
  | 'user'
  | 'accountant'
  | 'other'

export const PARTICIPANT_ROLES: ParticipantRole[] = [
  'decision_maker',
  'economic_buyer',
  'technical',
  'user',
  'accountant',
  'other',
]

export interface ContactDeal {
  dealId: string
  number: string
  title: string
  status: string
  role: ParticipantRole
  note: string | null
  primary: boolean
  /** Стадия сделки; нет поля — у сделки нет стадии (не бывает в штатной воронке). */
  stageId?: string | null
  stageName?: string | null
  stageType?: 'open' | 'won' | 'lost' | null
  /** Сумма в валюте сделки: сделки в разных валютах экран не складывает. */
  amount?: number | null
  currency?: string | null
  expectedCloseOn?: string | null
}

export interface ContactCard {
  id: string
  version: number
  lastName: string
  firstName: string | null
  fullName: string
  jobTitle: string | null
  email: string | null
  phone: string | null
  counterparty: { id: string; name: string } | null
  deals: ContactDeal[]
  middleName?: string | null
  mobilePhone?: string | null
  telegram?: string | null
  primary?: boolean
  createdAt?: string | null
}

export interface NewContact {
  counterpartyId: string
  lastName: string
  firstName?: string
  jobTitle?: string
  phone?: string
  email?: string
  primary?: boolean
}

export interface Participant {
  contactId: string
  fullName: string | null
  jobTitle: string | null
  phone: string | null
  email: string | null
  role: ParticipantRole
  note: string | null
  primary: boolean
  deleted: boolean
}

export function fetchContactCard(id: string, signal?: AbortSignal): Promise<ContactCard> {
  return request<ContactCard>(`/sales/contacts/${id}`, { signal })
}

export function createContact(body: NewContact): Promise<ContactCard> {
  return request<ContactCard>('/sales/contacts', { method: 'POST', body })
}

/**
 * Сохранение карточки контакта: один доменный PUT с версией вместо записи по колонке
 * (общая логика — `makeDomainSave`).
 */
export const saveContact = makeDomainSave((id) => `/sales/contacts/${id}`, {
  // Text fields clear with an empty string: null means "unchanged" on the server.
  last_name: { field: 'lastName', convert: asClearableString },
  first_name: { field: 'firstName', convert: asClearableString },
  middle_name: { field: 'middleName', convert: asClearableString },
  job_title: { field: 'jobTitle', convert: asClearableString },
  email: { field: 'email', convert: asClearableString },
  phone: { field: 'phone', convert: asClearableString },
  mobile_phone: { field: 'mobilePhone', convert: asClearableString },
  telegram: { field: 'telegram', convert: asClearableString },
  is_primary: 'primary',
  counterparty_id: 'counterpartyId',
})

export function fetchParticipants(dealId: string, signal?: AbortSignal): Promise<Participant[]> {
  return request<{ participants: Participant[] }>(`/sales/deals/${dealId}/contacts`, {
    signal,
  }).then((r) => r.participants)
}

export function addParticipant(
  dealId: string,
  body: { contactId: string; role: ParticipantRole; primary?: boolean },
): Promise<Participant[]> {
  return request<{ participants: Participant[] }>(`/sales/deals/${dealId}/contacts`, {
    method: 'POST',
    body,
  }).then((r) => r.participants)
}

export function removeParticipant(dealId: string, contactId: string): Promise<Participant[]> {
  return request<{ participants: Participant[] }>(`/sales/deals/${dealId}/contacts/${contactId}`, {
    method: 'DELETE',
  }).then((r) => r.participants)
}

/**
 * Удаление контакта — мягкое. Сервер откажет, пока человек участвует в открытых сделках:
 * переговоры молча лишились бы собеседника.
 */
export function deleteContact(id: string): Promise<void> {
  return request<void>(`/sales/contacts/${id}`, { method: 'DELETE' })
}
